import { SizeContextProvider } from '@app/components/cms/context/SizeContext';
import { OrderBottomBar } from '@app/components/orders/OrderBottomBar';
import { OrderTopBar } from '@app/components/orders/OrderTopBar';
import { usePageProductsContext } from '@app/components/pages/context/PageProductsContext';
import { PageSections } from '@app/components/pages/page/PageSections';
import { ProductDetailsModalContextProvider } from '@app/components/products/ProductDetails/context/ProductDetailsModalContext';
import { ProductDetailsModal } from '@app/components/products/ProductDetails/ProductDetailsModal';
import { PageTitle } from '@app/components/various/PageTitle';
import { getBuyerIdFromCurrentOrder, getOrderNumber } from '@app/ducks';
import { usePageBySlug } from '@app/services/hooks/pages/usePageBySlug';
import { useCustomStylesLoader } from '@app/utils/hooks/cms/useCustomStylesLoader';
import { useDebouncedVisibility } from '@app/utils/hooks/useDebouncedVisibility';
import { useUrlProductId } from '@app/utils/hooks/useUrlProductId';
import { useUrlVariantId } from '@app/utils/hooks/useUrlVariantId';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { listStylesToRecord } from '@app/utils/normalize';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  pageSlug: string;
  orderId: string;
}

export const PageContent = ({ pageSlug, orderId }: Props) => {
  const { t } = useTranslation(['selections']);
  const orderNumber = useSelector(getOrderNumber);
  const buyerIdFromCurrentOrder = useSelector(getBuyerIdFromCurrentOrder);
  const { data, isLoading } = usePageBySlug({ buyerId: buyerIdFromCurrentOrder, pageSlug });
  const buttonStyles = React.useMemo(() => listStylesToRecord(data?.buttonStyles ?? [], 'className'), [data]);
  const textStyles = React.useMemo(() => listStylesToRecord(data?.textStyles ?? [], 'className'), [data]);
  const { products } = usePageProductsContext();
  const productId = useUrlProductId();
  const variantId = useUrlVariantId();
  const hasProductId = isDefined(productId) && !isEmpty(productId);
  const shouldDisplayBottomBar = useDebouncedVisibility(hasProductId);
  useCustomStylesLoader(textStyles, buttonStyles);

  const sections = React.useMemo(() => data?.sections ?? [], [data]);
  const pageTitle = `${t('selections:selection_number', { orderNumber })} - ${data?.pageTitle}`;

  return (
    <PageTitle title={pageTitle}>
      <SizeContextProvider>
        <OrderTopBar isCmsPage orderId={orderId} />
        <OrderBottomBar
          location="page"
          orderId={orderId}
          hidden={!shouldDisplayBottomBar}
          className="px-4 md:pl-6 lg:pl-[max(2rem,calc((100%-1280px)/2))] lg:pr-[max(2rem,calc((100%-1280px)/2))]"
        />
        <PageSections sections={sections} isLoading={isLoading} />
      </SizeContextProvider>
      <ProductDetailsModalContextProvider productId={productId} products={Object.values(products)} variantId={variantId}>
        <ProductDetailsModal modalLocation="page" />
      </ProductDetailsModalContextProvider>
    </PageTitle>
  );
};
